import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { RevealOnScroll } from "../shared";
import {
  AGENCY_THESIS,
  VALUATION_SNAPSHOT,
  DIRECT_BENEFITS,
  INDIRECT_BENEFITS,
  VALUATION_MERGE,
} from "./agencyBenefitsContent";

const ENGINE_GROUPS = [
  {
    id: "cash",
    label: "Cash Engine",
    sublabel: "Pure money hitting your account every month",
    benefits: DIRECT_BENEFITS,
  },
  {
    id: "moat",
    label: "Moat Engine",
    sublabel: "Retention, defensibility, and capitalization",
    benefits: INDIRECT_BENEFITS,
  },
];

const ValuationSnapshotInline = () => (
  <p className="text-sm text-partner-muted font-body mb-8" data-testid="valuation-snapshot">
    {VALUATION_SNAPSHOT.map((stat, i) => (
      <React.Fragment key={stat.label}>
        {i > 0 && " · "}
        <span className="text-partner-accent font-semibold font-mono">{stat.value}</span> {stat.label}
        <span className="text-partner-muted/80"> ({stat.sub})</span>
      </React.Fragment>
    ))}
  </p>
);

const ValuationSnapshotCards = () => (
  <div
    className="grid grid-cols-3 gap-2 sm:gap-3 mb-8"
    data-testid="valuation-snapshot-mobile"
  >
    {VALUATION_SNAPSHOT.map((stat) => (
      <div
        key={stat.label}
        className="rounded-lg border border-[#2A3348] bg-[#11151F] px-2.5 py-3 text-center"
      >
        <p className="font-mono text-sm font-bold text-partner-accent leading-none">{stat.value}</p>
        <p className="text-[11px] text-partner-muted font-body mt-1.5 leading-tight">{stat.label}</p>
        <p className="text-[10px] text-partner-muted/70 font-body mt-0.5">{stat.sub}</p>
      </div>
    ))}
  </div>
);

const BenefitDetailPanel = ({ benefit, onClose }) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: "auto" }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.25 }}
    className="overflow-hidden"
    id={`agency-detail-panel-${benefit.id}`}
    data-testid={`agency-detail-${benefit.id}`}
  >
    <div className="mt-2 rounded-xl border border-[#00D2FF]/20 bg-[#11151F] p-4 sm:p-5 relative">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 w-8 h-8 rounded-lg border border-[#2A3348] bg-[#080A0F] flex items-center justify-center text-partner-muted hover:text-partner hover:border-partner transition-colors"
        aria-label="Close benefit details"
        data-testid={`agency-detail-close-${benefit.id}`}
      >
        <X className="w-4 h-4" />
      </button>
      <span className="font-mono text-xs text-partner-accent">{benefit.code}</span>
      <h4 className="text-base sm:text-lg font-bold text-partner mt-1.5 mb-3 pr-10 leading-snug">
        {benefit.title}
      </h4>
      <p className="text-sm text-partner-muted font-body leading-relaxed">{benefit.description}</p>
    </div>
  </motion.div>
);

const BenefitRow = ({ benefit, isOpen, onToggle, onClose }) => (
  <div data-testid={`agency-cell-${benefit.id}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`agency-detail-panel-${benefit.id}`}
      className={`w-full text-left px-3 py-3 rounded-lg transition-colors flex items-start gap-3 ${
        isOpen
          ? "bg-[#00D2FF]/8 border border-[#00D2FF]/25"
          : "hover:bg-[#11151F] border border-transparent"
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-mono text-partner-accent leading-snug">{benefit.code}</p>
        <p
          className={`text-sm leading-snug mt-0.5 font-body ${
            isOpen ? "text-partner font-semibold" : "text-partner-muted"
          }`}
        >
          {benefit.highlight}
        </p>
      </div>
      <ChevronDown
        className={`w-4 h-4 flex-shrink-0 mt-1 text-partner-accent/70 transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && <BenefitDetailPanel benefit={benefit} onClose={onClose} />}
    </AnimatePresence>
  </div>
);

const EngineSection = ({ group, openBenefitId, onToggle, onClose }) => (
  <div
    className="rounded-xl border border-[#2A3348] bg-[#11151F]/60 overflow-hidden"
    data-testid={`agency-engine-${group.id}`}
  >
    <div className="px-4 sm:px-5 py-4 border-b border-[#2A3348]">
      <h4 className="font-mono text-sm sm:text-base font-bold text-partner-accent tracking-[0.12em] uppercase">
        {group.label}
      </h4>
      <p className="text-xs sm:text-sm text-partner-muted font-body mt-1.5">{group.sublabel}</p>
    </div>
    <div className="p-2 sm:p-3 space-y-0.5">
      {group.benefits.map((benefit) => (
        <BenefitRow
          key={benefit.id}
          benefit={benefit}
          isOpen={openBenefitId === benefit.id}
          onToggle={() => onToggle(benefit.id)}
          onClose={onClose}
        />
      ))}
    </div>
  </div>
);

const AgencyBenefitsSection = () => {
  const [openBenefitId, setOpenBenefitId] = useState(null);

  const openBenefit = useMemo(
    () =>
      openBenefitId
        ? [...DIRECT_BENEFITS, ...INDIRECT_BENEFITS].find((b) => b.id === openBenefitId)
        : null,
    [openBenefitId]
  );

  const handleToggle = (benefitId) => {
    setOpenBenefitId((current) => (current === benefitId ? null : benefitId));
  };

  const handleClose = () => setOpenBenefitId(null);

  return (
    <section
      className="section-padding mesh-gradient relative overflow-hidden"
      data-testid="agency-benefits"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <RevealOnScroll>
          <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
            {AGENCY_THESIS.headline.split("Your Agency")[0]}
            <span className="gradient-text">Your Agency</span>
          </h2>
          <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-8">
            {AGENCY_THESIS.insight}
          </p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.08}>
          <div className="lg:hidden">
            <ValuationSnapshotCards />
          </div>
          <div className="hidden lg:block">
            <ValuationSnapshotInline />
          </div>
        </RevealOnScroll>

        <RevealOnScroll delay={0.12}>
          <div data-testid="twin-engine-console">
            <h3 className="text-xl sm:text-2xl font-bold text-partner mb-1">
              Two engines. <span className="gradient-text">One valuation lift.</span>
            </h3>
            <p className="text-sm text-partner-muted font-body mb-6">
              {DIRECT_BENEFITS.length} cash benefits · {INDIRECT_BENEFITS.length} moat benefits. Tap
              a benefit to read more
            </p>

            <div className="space-y-4 sm:space-y-5" data-testid="twin-engine-accordion">
              {ENGINE_GROUPS.map((group) => (
                <EngineSection
                  key={group.id}
                  group={group}
                  openBenefitId={openBenefitId}
                  onToggle={handleToggle}
                  onClose={handleClose}
                />
              ))}
            </div>

            {openBenefit && (
              <p className="sr-only" aria-live="polite">
                Showing details for {openBenefit.title}
              </p>
            )}

            <p className="mt-8 text-sm text-partner font-body leading-relaxed max-w-3xl">
              <span className="font-semibold text-partner-accent">{VALUATION_MERGE.label}:</span>{" "}
              {VALUATION_MERGE.text}
            </p>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

export default AgencyBenefitsSection;
